import { useState, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { calcOrm, fmtDate, fmtWeight, wtLabel, normalize } from "../helpers";

const RANGE_PRESETS = [
  { label:"30D", getDates:()=>{ const t=new Date(),f=new Date(t); f.setDate(f.getDate()-29); return {from:f.toISOString().split("T")[0],to:t.toISOString().split("T")[0]}; } },
  { label:"90D", getDates:()=>{ const t=new Date(),f=new Date(t); f.setDate(f.getDate()-89); return {from:f.toISOString().split("T")[0],to:t.toISOString().split("T")[0]}; } },
  { label:"1Y",  getDates:()=>{ const t=new Date(); return {from:`${t.getFullYear()}-01-01`,to:t.toISOString().split("T")[0]}; } },
  { label:"ALL", getDates:()=>({from:"2000-01-01",to:"2099-12-31"}) },
];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background:"var(--bg-highest)", border:"1px solid rgba(68,71,76,0.4)",
      borderRadius:8, padding:"8px 12px",
    }}>
      <div style={{ fontSize:9, color:"var(--muted)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>{label}</div>
      <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:14, fontWeight:700, color:"#b9c8de" }}>{payload[0].value}</div>
    </div>
  );
}

function ExercisePicker({ exercises, value, onChange }) {
  const [search, setSearch] = useState("");
  const [open, setOpen]     = useState(false);

  const filtered = search.trim()
    ? exercises.filter(e => normalize(e.name).includes(normalize(search)))
    : exercises;

  const select = (ex) => { onChange(ex.id); setSearch(""); setOpen(false); };

  return (
    <div style={{ position:"relative" }}>
      <div style={{ position:"relative" }}>
        <svg style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          type="text"
          value={search}
          placeholder={value?.name || "Search exercise..."}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          style={{
            width:"100%", boxSizing:"border-box",
            background:"var(--bg-low)", border:"1px solid rgba(68,71,76,0.3)",
            borderRadius:8, padding:"13px 16px 13px 40px",
            fontFamily:"'Space Grotesk',sans-serif",
            fontSize:12, fontWeight:600, letterSpacing:"0.05em",
            color:"var(--text)", outline:"none",
          }}
        />
      </div>
      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:60,
          background:"var(--bg-highest)", border:"1px solid rgba(68,71,76,0.3)",
          borderRadius:8, maxHeight:220, overflowY:"auto",
          boxShadow:"0 8px 24px rgba(0,0,0,0.4)",
        }}>
          {filtered.length === 0
            ? <div style={{ padding:"12px 16px", fontSize:12, color:"#64748b" }}>No matches</div>
            : filtered.map(ex => (
              <div key={ex.id} onMouseDown={() => select(ex)} style={{
                padding:"11px 16px", cursor:"pointer", fontSize:12, fontWeight:600,
                letterSpacing:"0.03em",
                borderBottom:"1px solid rgba(68,71,76,0.1)",
                color: value?.id === ex.id ? "#b9c8de" : "var(--text)",
                background: value?.id === ex.id ? "rgba(185,200,222,0.07)" : "transparent",
              }}>{ex.name}</div>
            ))
          }
        </div>
      )}
    </div>
  );
}

export default function ProgressView({ exercises, units = "lbs" }) {
  const [exId, setExId]         = useState(exercises[0]?.id || "");
  const [preset, setPreset]     = useState("30D");
  const [fromDate, setFromDate] = useState(() => { const t=new Date(),f=new Date(t); f.setDate(f.getDate()-29); return f.toISOString().split("T")[0]; });
  const [toDate, setToDate]     = useState(() => new Date().toISOString().split("T")[0]);
  const [metric, setMetric]     = useState("weight");

  const applyPreset = (p) => {
    setPreset(p.label);
    const { from, to } = p.getDates();
    setFromDate(from); setToDate(to);
  };

  const sel          = exercises.find(e => e.id === exId);
  const allSets      = sel?.sets || [];
  const filteredSets = sel
    ? allSets.filter(s => s.date >= fromDate && s.date <= toDate).sort((a,b) => a.date.localeCompare(b.date))
    : [];

  const byDate = {};
  filteredSets.forEach(s => {
    if (!byDate[s.date] || s.weight > byDate[s.date].weight) byDate[s.date] = s;
  });
  const chartData = Object.entries(byDate).map(([date, s]) => ({
    name: new Date(date+"T00:00:00").toLocaleDateString("en-US", { month:"short", day:"numeric" }),
    Weight: fmtWeight(s.weight, units),
    Volume: Math.round(s.reps * s.weight),
    Reps: s.reps,
    "Est 1RM": calcOrm(s.weight, s.reps) || 0,
  }));

  const metricKey = { weight:"Weight", volume:"Volume", reps:"Reps", orm:"Est 1RM" }[metric] || "Weight";
  const bestSet   = allSets.reduce((b,s) => s.weight>(b?.weight||0)||(s.weight===(b?.weight||0)&&s.reps>(b?.reps||0))?s:b, null);
  const bestOrm   = allSets.reduce((b,s) => { const o=calcOrm(s.weight,s.reps)||0; return o>b?o:b; }, 0);
  const totVol    = allSets.reduce((a,s) => a+s.reps*s.weight, 0);

  return (
    <div className="fade-in">

      {/* ── EXERCISE PICKER ── */}
      <section style={{ marginBottom: 16 }}>
        <ExercisePicker
          exercises={exercises}
          value={sel || null}
          onChange={id => setExId(id)}
        />
      </section>

      {/* ── RANGE TABS ── */}
      <section style={{ marginBottom: 20 }}>
        <div style={{ display:"flex", gap:4, padding:4, background:"var(--bg-lowest)", borderRadius:10 }}>
          {RANGE_PRESETS.map(p => (
            <button key={p.label} onClick={() => applyPreset(p)} style={{
              flex:1, padding:"8px 4px", borderRadius:8,
              background: preset===p.label ? "var(--bg-highest)" : "transparent",
              border:"none",
              color: preset===p.label ? "#b9c8de" : "#64748b",
              fontFamily:"'Space Grotesk',sans-serif",
              fontSize:10, fontWeight:700, letterSpacing:"0.1em",
              cursor:"pointer", transition:"all 0.15s",
            }}>{p.label}</button>
          ))}
        </div>
      </section>

      {sel ? (
        <>
          {/* ── STAT ROW ── */}
          <section style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:16 }}>
            {[
              { label:"EST 1RM",    value: bestOrm ? `${fmtWeight(bestOrm, units)}` : "—",  unit: wtLabel(units), highlight: true },
              { label:"DECLARED",   value: sel.oneRepMax ? `${fmtWeight(sel.oneRepMax, units)}` : "—", unit: wtLabel(units) },
              { label:"TOP SET",    value: bestSet ? `${fmtWeight(bestSet.weight, units)}` : "—", unit: wtLabel(units) },
              { label:"TOTAL VOL",  value: totVol > 999 ? `${(totVol/1000).toFixed(1)}K` : String(Math.round(totVol)), unit: wtLabel(units) },
            ].map(({ label, value, unit, highlight }) => (
              <div key={label} style={{
                background: highlight ? "rgba(185,200,222,0.07)" : "var(--bg-low)",
                borderRadius:8, padding:"12px 10px", textAlign:"center",
                border: highlight ? "1px solid rgba(185,200,222,0.15)" : "1px solid transparent",
              }}>
                <div style={{ fontSize:8, color:"var(--muted)", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:6 }}>{label}</div>
                <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:16, fontWeight:700, color: highlight ? "#b9c8de" : "var(--text)", lineHeight:1 }}>
                  {value || "—"}
                </div>
                {value && <div style={{ fontSize:8, color:"var(--muted)", marginTop:3, textTransform:"uppercase" }}>{unit}</div>}
              </div>
            ))}
          </section>

          {/* ── CHART ── */}
          <section style={{ background:"var(--bg-low)", borderRadius:8, padding:20, marginBottom:16 }}>
            {/* Metric tabs */}
            <div style={{ display:"flex", gap:4, marginBottom:16 }}>
              {[["weight","WEIGHT"],["volume","VOLUME"],["reps","REPS"],["orm","EST 1RM"]].map(([k,l]) => (
                <button key={k} onClick={() => setMetric(k)} style={{
                  flex:1, padding:"7px 4px", borderRadius:6,
                  background: metric===k ? "rgba(185,200,222,0.12)" : "transparent",
                  border:`1px solid ${metric===k ? "rgba(185,200,222,0.25)" : "rgba(68,71,76,0.25)"}`,
                  color: metric===k ? "#b9c8de" : "#64748b",
                  fontFamily:"'Space Grotesk',sans-serif",
                  fontSize:9, fontWeight:700, letterSpacing:"0.06em", cursor:"pointer",
                  transition:"all 0.15s",
                }}>{l}</button>
              ))}
            </div>

            {chartData.length < 1 ? (
              <div style={{ textAlign:"center", padding:"32px 0" }}>
                <div style={{ fontSize:10, color:"var(--muted)", letterSpacing:"0.15em", textTransform:"uppercase" }}>NO DATA IN RANGE</div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData} margin={{ top:4, right:8, left:-20, bottom:0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false}/>
                  <XAxis dataKey="name" tick={{ fill:"#64748b", fontSize:9, fontFamily:"'Space Grotesk',sans-serif" }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fill:"#64748b", fontSize:9, fontFamily:"'Space Grotesk',sans-serif" }} axisLine={false} tickLine={false}/>
                  <Tooltip content={<ChartTooltip/>}/>
                  <Line type="monotone" dataKey={metricKey} stroke="#b9c8de" strokeWidth={2.5}
                    dot={{ fill:"#b9c8de", r:3, strokeWidth:0 }} connectNulls/>
                </LineChart>
              </ResponsiveContainer>
            )}
          </section>

          {/* ── SET HISTORY ── */}
          {filteredSets.length > 0 && (
            <section style={{ marginBottom:16 }}>
              <div style={{ fontSize:9, color:"var(--muted)", letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, marginBottom:10 }}>
                SET HISTORY
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                {[...filteredSets].reverse().map(s => {
                  const est = calcOrm(s.weight, s.reps);
                  return (
                    <div key={s.id} style={{
                      display:"grid", gridTemplateColumns:"1.2fr 1fr 1fr 1fr",
                      background:"var(--bg-low)", borderRadius:8,
                      padding:"10px 14px", alignItems:"center",
                    }}>
                      <span style={{ fontSize:10, color:"var(--muted)" }}>{s.date ? fmtDate(s.date) : "—"}</span>
                      <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:13, fontWeight:700, color:"#b9c8de", textAlign:"center" }}>
                        {s.weight ? fmtWeight(s.weight, units) : "BW"}
                        <span style={{ fontSize:8, marginLeft:2, color:"var(--muted)", textTransform:"uppercase" }}>{wtLabel(units)}</span>
                      </span>
                      <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:13, fontWeight:600, textAlign:"center", color:"var(--text)" }}>
                        {s.reps}<span style={{ fontSize:8, marginLeft:2, color:"var(--muted)" }}>reps</span>
                      </span>
                      <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:12, fontWeight:700, color:"var(--muted)", textAlign:"right" }}>
                        {est ? `${est}` : "—"}
                        {est ? <span style={{ fontSize:8, marginLeft:2, textTransform:"uppercase" }}>{wtLabel(units)}</span> : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      ) : (
        <div style={{ background:"var(--bg-low)", borderRadius:8, padding:"40px 20px", textAlign:"center" }}>
          <div style={{ fontSize:10, color:"var(--muted)", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>NO EXERCISES</div>
          <div style={{ fontSize:12, color:"var(--muted)" }}>Add exercises on the Exercises page</div>
        </div>
      )}
    </div>
  );
}
